import React, { useState } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { toast } from 'react-toastify';

const InterviewScheduler = ({ formData, onClose }) => {
  const [selectedDates, setSelectedDates] = useState([]);
  const [timeSlots, setTimeSlots] = useState({});
  const [comments, setComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const availableTimeSlots = [
    '09:00', '10:00', '11:00', '13:00', '14:00', '15:00', '16:00', '17:00'
  ];

  const handleDateChange = (date) => {
    const dateString = date.toISOString().split('T')[0];
    if (selectedDates.includes(dateString)) {
      setSelectedDates(selectedDates.filter(d => d !== dateString));
      const newTimeSlots = { ...timeSlots };
      delete newTimeSlots[dateString];
      setTimeSlots(newTimeSlots);
    } else if (selectedDates.length < 5) {
      setSelectedDates([...selectedDates, dateString]);
    } else {
      toast.warn('最大5つまでの候補日を選択してください');
    }
  };

  const handleTimeSlotChange = (date, time) => {
    setTimeSlots({
      ...timeSlots,
      [date]: time
    });
  };

  const isDateDisabled = ({ date, view }) => {
    if (view === 'month') {
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      
      // 過去の日付と今日を無効化
      if (date < tomorrow) return true;
      
      // 土日を無効化
      const dayOfWeek = date.getDay();
      return dayOfWeek === 0 || dayOfWeek === 6;
    }
    return false;
  };

  const handleSubmit = async () => {
    if (selectedDates.length === 0) {
      toast.error('少なくとも1つの候補日を選択してください');
      return;
    }

    const incompleteDates = selectedDates.filter(date => !timeSlots[date]);
    if (incompleteDates.length > 0) {
      toast.error('すべての候補日に時間を設定してください');
      return;
    }

    setIsSubmitting(true);

    try {
      const scheduleData = {
        ...formData,
        selectedDates: selectedDates.map(date => ({
          date,
          time: timeSlots[date]
        })),
        comments,
        type: 'interview-schedule'
      };

      // Slack通知を送信
      const response = await fetch('/api/send-slack-notification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          formData: scheduleData,
          formType: 'interview'
        })
      });

      if (response.ok) {
        // メール送信（EmailJS）
        const emailResponse = await fetch('/api/send-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            formData: scheduleData,
            formType: 'interview'
          })
        });

        if (emailResponse.ok) {
          toast.success('面接日程の調整申請を送信しました');
          onClose();
        } else {
          throw new Error('メール送信に失敗しました');
        }
      } else {
        throw new Error('通知送信に失敗しました');
      }
    } catch (error) {
      console.error('送信エラー:', error);
      toast.error('送信に失敗しました。もう一度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto border border-blue-500">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-white">面接日程調整</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-2xl transition-colors"
            >
              ×
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold mb-4 text-white">希望日時を選択してください（最大5つ）</h3>
              <div className="calendar-container">
                <Calendar
                  onChange={handleDateChange}
                  value={null}
                  tileDisabled={isDateDisabled}
                  tileClassName={({ date }) => {
                    const dateString = date.toISOString().split('T')[0];
                    return selectedDates.includes(dateString) ? 'selected-date' : '';
                  }}
                  minDate={new Date()}
                  locale="ja-JP"
                />
              </div>
              <p className="text-sm text-gray-400 mt-2">
                ※平日のみ選択可能です
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4 text-white">選択された候補日</h3>
              {selectedDates.length === 0 ? (
                <p className="text-gray-400">候補日を選択してください</p>
              ) : (
                <div className="space-y-4">
                  {selectedDates.map((date, index) => (
                    <div key={date} className="border border-blue-500 rounded-lg p-4 bg-slate-700">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium text-white">
                          {new Date(date).toLocaleDateString('ja-JP', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            weekday: 'short'
                          })}
                        </span>
                        <button
                          onClick={() => {
                            setSelectedDates(selectedDates.filter(d => d !== date));
                            const newTimeSlots = { ...timeSlots };
                            delete newTimeSlots[date];
                            setTimeSlots(newTimeSlots);
                          }}
                          className="text-red-400 hover:text-red-300 transition-colors"
                        >
                          削除
                        </button>
                      </div>
                      <select
                        value={timeSlots[date] || ''}
                        onChange={(e) => handleTimeSlotChange(date, e.target.value)}
                        className="w-full border border-gray-600 rounded px-3 py-2 bg-slate-800 text-white"
                        required
                      >
                        <option value="">時間を選択</option>
                        {availableTimeSlots.map(time => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-6">
                <label className="block text-sm font-medium text-white mb-2">
                  備考・希望事項
                </label>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  rows={4}
                  className="w-full border border-gray-600 rounded px-3 py-2 bg-slate-800 text-white"
                  placeholder="その他ご希望やご質問があればお書きください"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-4 mt-6 pt-6 border-t border-gray-600">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-600 rounded-lg hover:bg-gray-700 text-white transition-colors"
              disabled={isSubmitting}
            >
              キャンセル
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || selectedDates.length === 0}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? '送信中...' : '面接日程を申請'}
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        .calendar-container .react-calendar {
          background: #1e293b;
          border: 1px solid #3b82f6;
          border-radius: 8px;
          color: white;
          font-family: inherit;
        }
        
        .calendar-container .react-calendar__navigation button {
          background: #374151;
          color: white;
          border: none;
          font-size: 16px;
          font-weight: 600;
        }
        
        .calendar-container .react-calendar__navigation button:hover {
          background: #4b5563;
        }
        
        .calendar-container .react-calendar__navigation button:disabled {
          background: #1f2937;
          color: #6b7280;
        }
        
        .calendar-container .react-calendar__month-view__weekdays {
          background: #374151;
        }
        
        .calendar-container .react-calendar__month-view__weekdays__weekday {
          color: #d1d5db;
          font-weight: 600;
          text-decoration: none;
        }
        
        .calendar-container .react-calendar__tile {
          background: #1e293b;
          color: white;
          border: 1px solid #374151;
          padding: 12px 0;
        }
        
        .calendar-container .react-calendar__tile:hover {
          background: #374151;
        }
        
        .calendar-container .react-calendar__tile:disabled {
          background: #0f172a;
          color: #6b7280;
        }
        
        .calendar-container .react-calendar__tile--active {
          background: #3b82f6 !important;
          color: white !important;
        }
        
        .calendar-container .selected-date {
          background: #10b981 !important;
          color: white !important;
        }
        
        .calendar-container .react-calendar__tile--now {
          background: #1e40af;
          color: white;
        }
        
        .calendar-container .react-calendar__tile--hasActive {
          background: #3b82f6;
        }
      `}</style>
    </div>
  );
};

export default InterviewScheduler; 